import { moveItemInArray } from '@angular/cdk/drag-drop';
import { AdStudioContainerTabState } from '../../../../_core/state/ad/ad-studio-container/ad-studio-container.model';
import { StylesContainerConfig } from '../../../../../../../api/src/creative-unit-package/models/creative-unit-package.dto';
import { StyleContainerState } from '../../../../_core/state/ad/ad-styles/ad-styles-container/ad-styles-container.component';
import { CreativeUnitWithCustomizationsDto } from '../../../../../../../api/src/creative-unit-package/models/creative-unit/creative-unit.dto';

export class Tab {
	type: AdStudioContainerTabState;
	label: string;
	data: {
		style?: {
			container: StylesContainerConfig
			state: StyleContainerState
		}
		creativeUnit: CreativeUnitWithCustomizationsDto
	}
}

export enum SidebarType {
	ADDITIONAL = 'additional',
	MAIN = 'main'
}

export enum SidebarPropertyViewMode {
	BASIC = 'basic',
	ADVANCED = 'advanced'
}

export class Sidebar {
	type: SidebarType = SidebarType.ADDITIONAL
	tabs: Tab[];
	active: number;
	minimized: boolean = false;
	width?: number = 0;
}

export const defaultSidebar: Sidebar = {
	type: SidebarType.MAIN,
	tabs: [{
		type: null,
		label: null,
		data: null
	}],
	active: 0,
	minimized: false,
	width: 0
}

/**
 * AdEditorState
 * @description
 * This is the state for the AdEditorComponent. It extends the AdStudioContainerState
 * to add some properties specific to the editor experience.
 */
export class AdEditorSidebarState {

	/** Additional sidebars created by pinning windows */
	public sidebarState: Sidebar[] = [];

	/** The width of the default sidebar within a sidebar container with additional sidebars */
	public defaultPageSidebarWidth: number = 0;

	/** Track which tab we are currently dragging, to know whether it was dropped outside the sidebar container */
	public draggedTab: { sidebarIndex: number, tabIndex: number };

	constructor() {}

	/**
	 * Creates a new tab in the rightmost sidebar.
	 * if there is no active sidebar to push to, then also creates the first sidebar
	 */
	addSidebarTab(tab: Tab) {
		let closestSidebar = this.sidebarState?.slice()?.reverse()?.findIndex(s => !s.minimized);

		console.log('addSidebarTab', this.sidebarState, tab, closestSidebar);

		if (closestSidebar === -1) {
			this.sidebarState.push({
				type: SidebarType.ADDITIONAL,
				tabs: [tab],
				active: 0,
				minimized: false
			});

		} else {
			// re-reverse the index to get the correct one, as we reversed the index to look back to front
			closestSidebar = (this.sidebarState.length - 1) - closestSidebar;

			this.sidebarState[closestSidebar].tabs.push(tab);
			this.sidebarState[closestSidebar].active = this.sidebarState[closestSidebar].tabs.length - 1;
			this.sidebarState[closestSidebar].minimized = false;
		}
	}

	/**
	 * Remove a sidebar tab, and if the sidebar is now empty, remove the sidebar.
	 */
	removeSidebarTab(sidebarIndex: number, tabIndex: number) {
		this.sidebarState[sidebarIndex].tabs.splice(tabIndex, 1);

		const tabs = this.sidebarState[sidebarIndex].tabs;

		if (tabs.length === 0) {
			this.sidebarState.splice(sidebarIndex, 1);

		} else {
			// updates the sidebar's active tab index if it was affected by the removal
			const activeTab = this.sidebarState[sidebarIndex].active;

			if (activeTab === tabIndex) {
				if (tabs[activeTab]) {
					this.sidebarState[sidebarIndex].active = activeTab;
				} else {
					this.sidebarState[sidebarIndex].active = activeTab - 1;
				}
			}

			if (activeTab > tabIndex) {
				this.sidebarState[sidebarIndex].active = activeTab - 1;
			}
		}
	}

	/**
	 * Minimizes a sidebar, which hides the individual tabs and shows a minimized version of the sidebar.
	 */
	minimizeSidebar(sidebarIndex: number) {
		this.sidebarState[sidebarIndex].minimized = true;
	}

	/**
	 * Sets a sidebar's width property.
	 */
	setSidebarWidth(sidebarIndex: number, width: number) {
		this.sidebarState[sidebarIndex].width = width;
	}

	/**
	 * Moves a tab:
	 * 1. To a different sidebar
	 * 2. Within the same sidebar
	 */
	moveSidebarTab(event: any, sidebarIndex: number) {
		// if the move is to a different sidebar, move the tab to the new sidebar
		if (this.draggedTab.sidebarIndex !== sidebarIndex) {
			const tab = this.sidebarState[this.draggedTab.sidebarIndex].tabs[this.draggedTab.tabIndex];

			this.sidebarState[sidebarIndex].tabs.splice(event.currentIndex, 0, tab);
			this.removeSidebarTab(this.draggedTab.sidebarIndex, this.draggedTab.tabIndex);

		} else if (event.previousIndex !== event.currentIndex) {
			// if the move is within the same sidebar, move the tab within the sidebar
			moveItemInArray(this.sidebarState[sidebarIndex].tabs, event.previousIndex, event.currentIndex);

			// update the active tab index if it was affected by the move
			const activeTab = this.sidebarState[sidebarIndex].active;

			if (event.previousIndex === activeTab) {
				this.sidebarState[sidebarIndex].active = event.currentIndex;
			}

			if (event.previousIndex > activeTab && event.currentIndex <= activeTab) {
				this.sidebarState[sidebarIndex].active = activeTab + 1;
			}

			if (event.previousIndex < activeTab && event.currentIndex >= activeTab) {
				this.sidebarState[sidebarIndex].active = activeTab - 1;
			}
		}
	}

	/**
	 * Set the tab that is currently being dragged to track whether it was dropped outside the sidebar container.
	 */
	setActiveDragItem(sidebarIndex: number, tabIndex: number) {
		this.draggedTab = {
			sidebarIndex: sidebarIndex,
			tabIndex: tabIndex
		};
	}

	/**
	 * Create a new sidebar from a dragged tab that is dropped outside the sidebar container.
	 */
	createdSidebarFromDraggedItem() {
		const sidebarIndex = this.draggedTab.sidebarIndex;
		const tabIndex = this.draggedTab.tabIndex;

		const draggedTab = this.sidebarState[sidebarIndex].tabs[tabIndex];

		this.removeSidebarTab(sidebarIndex, tabIndex);

		this.sidebarState.unshift({
			type: SidebarType.ADDITIONAL,
			tabs: [draggedTab],
			active: 0,
			minimized: false,
			width: 100 / (this.sidebarState.length + 2)
		});
	}

	/**
	 * Get the sidebar tabs labels as a delimited string.
	 */
	getSidebarTabs(sidebar: Sidebar, delimiter = '\n') {
		return sidebar.tabs.map(tab => {
			return (tab.data.style.state === 'unit' ? tab.data.creativeUnit.name : 'Package') + ': ' + tab.label;
		}).join(delimiter);
	}

	/**
	 * Get the index of the first visible sidebar (left to right), accounting for minimized sidebars.
	 */
	getFirstVisibleSidebarIndex() {
		const visibleSidebars = this.sidebarState?.filter(sidebar => !sidebar.minimized);

		if (visibleSidebars?.length > 0) {
			return this.sidebarState?.findIndex(sidebar => !sidebar.minimized);
		} else return this.sidebarState?.length;
	}

	isFirstVisibleSidebar(sidebarIndex: number) {
		return this.getFirstVisibleSidebarIndex() === sidebarIndex;
	}

}
